import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalLayout from '../../components/Portal/PortalLayout';
import TimeSlotPicker from '../../components/Portal/TimeSlotPicker';
import MemberPartnerSearch from '../../components/Portal/MemberPartnerSearch';
import { memberAPI } from '../../services/api';
import { cn } from '../../lib/utils';

/**
 * PreferencesEdit - Edit tournament preferences
 * Time slots, playing partners, ride/walk preference, special requests
 */
const PreferencesEdit = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    timeSlotPreference: 'no-preference',
    playingPartnerPreferences: [],
    rideOrWalkPreference: 'no-preference',
    specialRequests: '',
  });

  const [memberGhin, setMemberGhin] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const memberId = user.memberId;

  useEffect(() => {
    const fetchPreferences = async () => {
      if (!memberId) {
        setError('No member ID found. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        const response = await memberAPI.getPortalSummary(memberId);
        const member = response.data.data.member;
        const prefs = member.defaultPreferences || {};

        setMemberGhin(member.ghin);
        setFormData({
          timeSlotPreference: prefs.timeSlotPreference || 'no-preference',
          playingPartnerPreferences: prefs.playingPartnerPreferences || [],
          rideOrWalkPreference: prefs.rideOrWalkPreference || 'no-preference',
          specialRequests: prefs.specialRequests || '',
        });
      } catch (err) {
        console.error('Failed to load preferences:', err);
        setError('Failed to load your preferences. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [memberId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await memberAPI.updatePreferences(memberId, formData);
      setSuccess('Preferences saved successfully!');
      setTimeout(() => {
        navigate('/portal');
      }, 1500);
    } catch (err) {
      console.error('Failed to save preferences:', err);
      setError(err.response?.data?.message || 'Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const rideWalkOptions = [
    { value: 'ride', label: 'Ride (Cart)', description: 'I prefer to ride in a golf cart' },
    { value: 'walk', label: 'Walk', description: 'I prefer to walk the course' },
    { value: 'no-preference', label: 'No Preference', description: 'Either works for me' },
  ];

  if (loading) {
    return (
      <PortalLayout title="Preferences" showBack>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full"></div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout title="Preferences" showBack>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Time Slot Preference */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Tee Time Preference
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Select when you'd prefer to play. We'll try to accommodate your preference.
          </p>

          <TimeSlotPicker
            value={formData.timeSlotPreference}
            onChange={(value) => setFormData(prev => ({ ...prev, timeSlotPreference: value }))}
          />
        </section>

        {/* Playing Partners */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Preferred Playing Partners
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Search for members you'd like to be paired with. We'll try to group you together.
          </p>

          <MemberPartnerSearch
            selectedPartners={formData.playingPartnerPreferences}
            onPartnersChange={(partners) => setFormData(prev => ({ ...prev, playingPartnerPreferences: partners }))}
            maxPartners={3}
            excludeGhin={memberGhin}
          />
        </section>

        {/* Ride or Walk */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Ride or Walk
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Do you prefer to ride in a cart or walk the course?
          </p>

          <div className="space-y-2">
            {rideWalkOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, rideOrWalkPreference: option.value }))}
                className={cn(
                  'w-full p-4 rounded-lg border-2 text-left transition-all',
                  formData.rideOrWalkPreference === option.value
                    ? 'border-green-500 bg-green-50 ring-2 ring-green-500 ring-opacity-50'
                    : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'
                )}
              >
                <div className="font-medium text-gray-900">{option.label}</div>
                <div className="text-sm text-gray-500 mt-1">{option.description}</div>
              </button>
            ))}
          </div>
        </section>

        {/* Special Requests */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Special Requests
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Any other requests or notes for the tournament director.
          </p>

          <textarea
            value={formData.specialRequests}
            onChange={(e) => setFormData(prev => ({ ...prev, specialRequests: e.target.value }))}
            placeholder="e.g., Medical accommodation needed, prefer morning due to work schedule..."
            rows={4}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base resize-none"
          />
          <p className="text-xs text-gray-500 mt-1 text-right">
            {formData.specialRequests.length}/500 characters
          </p>
        </section>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
            {success}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={saving}
          className={cn(
            'w-full py-4 rounded-xl font-medium text-lg transition-colors',
            saving
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
          )}
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
              Saving...
            </span>
          ) : (
            'Save Preferences'
          )}
        </button>
      </form>
    </PortalLayout>
  );
};

export default PreferencesEdit;
